import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import styles from './DataDeletionModal.module.css';

type FormState = 'idle' | 'loading' | 'success' | 'error';

// Formspree handles routing — recipient address is configured in the dashboard only
const ENDPOINT = 'https://formspree.io/f/mpqybknv';

interface DataDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DataDeletionModal({ isOpen, onClose }: DataDeletionModalProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [email, setEmail] = useState('');
  const [state, setState] = useState<FormState>('idle');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEmail('');
    setState('idle');
    setTouched(false);
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  const hasValidEmail = useMemo(() => /\S+@\S+\.\S+/.test(email.trim()), [email]);
  const showError = touched && !hasValidEmail;

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!hasValidEmail || state === 'loading') return;

    setState('loading');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          _subject: 'Data Deletion Request — Green Rituals',
          request_type: 'data_deletion',
          message: `User requests deletion of all personal data associated with email: ${email.trim()}`,
        }),
      });
      if (!res.ok) { setState('error'); return; }
      setState('success');
    } catch {
      setState('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="deletion-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">×</button>

        {state === 'success' ? (
          <div className={styles.successWrap}>
            <ShieldCheck className={styles.successIcon} strokeWidth={1.6} aria-hidden="true" />
            <h3 id="deletion-modal-title" className={styles.title}>Request Received</h3>
            <p className={styles.text}>
              We've received your data deletion request.
              We'll process it within 30 days and confirm by email.
            </p>
            <button type="button" className={styles.submitButton} onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <ShieldCheck className={styles.headerIcon} strokeWidth={1.6} aria-hidden="true" />
              <h3 id="deletion-modal-title" className={styles.title}>Data Deletion Request</h3>
            </div>
            <p className={styles.text}>
              You have the right to request deletion of your personal data from our systems.
              Enter the email address you used to sign up and we'll remove all associated data within 30 days.
            </p>

            <form className={styles.form} onSubmit={onSubmit} noValidate>
              <label htmlFor="deletion-email" className={styles.label}>Your Email Address</label>
              <input
                ref={inputRef}
                id="deletion-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onBlur={() => setTouched(true)}
                onChange={(e) => setEmail(e.target.value)}
                className={`${styles.input} ${showError ? styles.inputError : ''}`.trim()}
                placeholder="you@example.com"
                aria-invalid={showError}
                aria-describedby={showError || state === 'error' ? 'deletion-error' : undefined}
              />
              {showError && (
                <p id="deletion-error" className={styles.error}>Please enter a valid email address.</p>
              )}
              {state === 'error' && (
                <p id="deletion-error" className={styles.error}>Something went wrong. Please try again.</p>
              )}
              <button type="submit" className={styles.submitButton} disabled={state === 'loading'}>
                {state === 'loading' ? 'Submitting...' : 'Submit Deletion Request'}
              </button>
              <p className={styles.microtext}>
                Your request will be processed within 30 days in accordance with applicable privacy laws (GDPR / CCPA).
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
